import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OnBoardingState, Preferences } from '@vendasta/meetings';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  INTEGRATIONS_PAGE_LINK_TOKEN,
  MEETING_INTEGRATION_SERVICE_KEYS_TOKEN,
  CALENDAR_VIEW_PAGE_LINK_TOKEN,
  PERSONAL_CALENDAR_ID_TOKEN,
  RECOMMENDED_PERSONAL_CALENDAR_SLUGS_TOKEN,
} from '../../data-providers/providers';
import { MeetingIntegrationServiceKeys } from '../../interface';
import { IntegrationServiceSelection } from '../../meeting-integrations/integration-selection/integration-selection.component';
import { ScheduleSettingsViewService, View } from '../schedule-settings-view.service';
import {
  POSTHOG_ACTIONS,
  POSTHOG_CATEGORIES,
  POSTHOG_KEYS,
  CALENDAR_SOURCE,
  getCalendarProviderDisplayName,
  getMeetingAppDisplayName,
} from '../../constants';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { AvailableServices } from '@vendasta/integrations';
import { AvailabilityCardComponent } from '../availability-card/availability-card.component';

interface StepLabels {
  step1: string;
  step2: string;
  step3: string;
  step4: string;
}

@Component({
  selector: 'meeting-scheduler-settings-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
  standalone: false,
})
export class WizardComponent implements OnInit {
  @Input() isHostConfigured: boolean;
  @Input() isGoogleMeetConfigured: boolean;
  @Input() isMicrosoftConfigured: boolean;
  @Input() hostPreferences: Preferences;
  @Input() initialSlug: string;
  @Input() hostId: string;

  @ViewChild('availabilityCard') availabilityCard: AvailabilityCardComponent;

  wizardStep$: Observable<number>;
  meetingAppSelection;

  readonly onMobile$: Observable<boolean>;
  stepLabels$: Observable<StepLabels>;
  private readonly selection$$: BehaviorSubject<IntegrationServiceSelection> = new BehaviorSubject(undefined);
  disableStep2Next$: Observable<boolean>;
  selectedCalendarProvider: string;
  selectedIntegrationConnected: boolean;

  private readonly wizardStepToOnBoardingState: Record<number, OnBoardingState> = {
    0: OnBoardingState.NOT_YET_STARTED,
    1: OnBoardingState.SLUG_CREATED,
    2: OnBoardingState.CALENDAR_CONNECTED,
    3: OnBoardingState.MEETING_APP_CONNECTED,
    4: OnBoardingState.COMPLETED_SETUP,
  };

  constructor(
    @Inject(MEETING_INTEGRATION_SERVICE_KEYS_TOKEN) public readonly SERVICE_KEYS: MeetingIntegrationServiceKeys,
    @Inject(INTEGRATIONS_PAGE_LINK_TOKEN) public readonly integrationsPageLink$: Observable<string>,
    @Inject(CALENDAR_VIEW_PAGE_LINK_TOKEN) public readonly myMeetingsPageLink$: Observable<string>,
    @Inject(RECOMMENDED_PERSONAL_CALENDAR_SLUGS_TOKEN) public readonly recommendedSlugs$: Observable<string[]>,
    @Inject(PERSONAL_CALENDAR_ID_TOKEN) public readonly personalCalendarId$: Observable<string>,
    private readonly scheduleSettingsViewService: ScheduleSettingsViewService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
  ) {
    this.meetingAppSelection = toSignal(this.scheduleSettingsViewService.meetingAppSelection$);
    this.onMobile$ = breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(map((result) => result.matches));
    this.stepLabels$ = this.onMobile$.pipe(
      map((onMobile) => {
        if (onMobile) {
          return { step1: '', step2: '', step3: '', step4: '' };
        }
        return {
          step1: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.STEPS.STEP_1'),
          step2: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.STEPS.STEP_2'),
          step3: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.STEPS.STEP_3'),
          step4: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.STEPS.STEP_4_V2'),
        };
      }),
    );
  }

  ngOnInit(): void {
    this.wizardStep$ = this.scheduleSettingsViewService.wizardStep$;
    this.analyticsService.trackEvent(POSTHOG_KEYS.SETUP_WIZARD_OPENED, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
  }

  onStepperSelectionChange(selection: StepperSelectionEvent): void {
    const step = selection.selectedIndex;

    this.scheduleSettingsViewService.setWizardStep(step);

    const onboardingState = this.wizardStepToOnBoardingState[step];
    if (onboardingState !== undefined) {
      this.meetingSchedulerStoreService.updateUserPreferences({
        onBoardingState: onboardingState,
      });
    }

    const posthogstep = selection.selectedIndex + 1;
    if (posthogstep != 1) {
      const previousStep = posthogstep - 1; // Marking previous step as completed
      const postHogKey = this.getPostHogKey(previousStep);
      this.analyticsService.trackEvent(postHogKey, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);
    }
  }

  setStep(step: number): void {
    this.scheduleSettingsViewService.setWizardStep(step);
  }

  onIntegrationSelection(selection: IntegrationServiceSelection): void {
    this.selection$$.next(selection);
    this.selectedIntegrationConnected = selection.isConnected;
  }

  async onAvailabilitySaved() {
    const myMeetingsPageLink = await firstValueFrom(this.myMeetingsPageLink$);
    const postHogKey = this.getPostHogKey(4);
    this.analyticsService.trackEvent(postHogKey, POSTHOG_CATEGORIES.USER, POSTHOG_ACTIONS.CLICK);

    if (this.selectedCalendarProvider) {
      this.analyticsService.trackEvent(
        POSTHOG_KEYS.CALENDAR_PROVIDER_SELECTED,
        POSTHOG_CATEGORIES.USER,
        POSTHOG_ACTIONS.CLICK,
        undefined,
        { calendar_or_meeting_provider: getCalendarProviderDisplayName(this.selectedCalendarProvider) },
      );
    }

    const currentSelection = this.selection$$.value;
    if (currentSelection) {
      this.analyticsService.trackEvent(
        POSTHOG_KEYS.MEETING_APP_SELECTED,
        POSTHOG_CATEGORIES.USER,
        POSTHOG_ACTIONS.CLICK,
        undefined,
        {
          calendar_or_meeting_provider: getMeetingAppDisplayName(currentSelection.service),
        },
      );
    }
    await this.meetingSchedulerStoreService.updateUserPreferences({
      onBoardingState: OnBoardingState.COMPLETED_SETUP,
    });

    this.scheduleSettingsViewService.setView(View.SETTINGS);
    await this.router.navigate([myMeetingsPageLink]);
  }

  getPostHogKey(step: number): string {
    const postHogKeysMap: Record<number, string> = {
      1: POSTHOG_KEYS.STEP_1_COMPLETED,
      2: POSTHOG_KEYS.STEP_2_COMPLETED,
      3: POSTHOG_KEYS.STEP_3_COMPLETED,
      4: POSTHOG_KEYS.STEP_4_COMPLETED,
    };
    return postHogKeysMap[step];
  }
  handleCalendarProvider(provider: string) {
    this.selectedCalendarProvider = provider;
    this.scheduleSettingsViewService.setMeetingAppSelection(AvailableServices.MICROSOFT);
    if (provider == CALENDAR_SOURCE.GOOGLE) {
      this.scheduleSettingsViewService.setMeetingAppSelection(AvailableServices.GOOGLE_MEET);
    }
  }
}
